/**
 * Where a drop-down list goes when the keyboard has taken half the screen.
 *
 * Samuel, 2026-09-24, building a material order on his Android: he searched
 * "nails", saw two framing nails, and could not scroll to the roofing nails he
 * wanted. The list opened below the box and ran 300px down; the keyboard
 * covered the bottom of the screen; and five rows of nails were shorter than
 * 300px, so the list had nothing to scroll and no swipe could bring the fifth
 * row up from behind the keyboard. Chrome on Android and Safari on iPhone both
 * lay the page out at full height with the keyboard open, so `window.innerHeight`
 * did not know the keyboard was there. `visualViewport` does.
 *
 * Pure geometry, so it is tested: given the box and the part of the screen
 * that is actually visible, say whether the list opens below or above and how
 * tall it may be. Below when there is room for it below; above when there is
 * more room above; never taller than the room on the side it took. All
 * numbers are in the layout viewport's coordinates, which is what
 * `getBoundingClientRect` and `position: fixed` share.
 */

export interface Box {
  top: number;
  bottom: number;
  left: number;
  width: number;
}

export interface VisibleArea {
  /** How far the visible part starts below the layout viewport's top. */
  top: number;
  height: number;
  left: number;
  width: number;
}

export interface Placement {
  /** Set for a list that opens below the box. */
  top?: number;
  /**
   * Set for a list that opens above it: the distance from the bottom of the
   * layout viewport, so the list grows upward from just over the box whatever
   * its height turns out to be.
   */
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

/** The gap between the box and the list. */
const GAP = 4;

/** Never wider than this margin off the visible edge. */
const EDGE = 8;

export function placeDropdown(
  anchor: Box,
  visible: VisibleArea,
  layoutHeight: number,
  {
    preferred = 300,
    minWidth = 300,
  }: { preferred?: number; minWidth?: number } = {},
): Placement {
  const visibleBottom = visible.top + visible.height;
  const roomBelow = Math.max(0, visibleBottom - anchor.bottom - GAP - EDGE);
  const roomAbove = Math.max(0, anchor.top - visible.top - GAP - EDGE);

  // Below unless below cannot hold the list and above can hold more of it.
  const below = roomBelow >= preferred || roomBelow >= roomAbove;
  const room = below ? roomBelow : roomAbove;

  // Keep the list on the visible screen sideways too: a box near the right
  // edge would otherwise open a 300px list half off the phone.
  const width = Math.min(
    Math.max(anchor.width, minWidth),
    Math.max(1, visible.width - EDGE * 2),
  );
  const maxLeft = visible.left + visible.width - width - EDGE;
  const left = Math.max(visible.left + EDGE, Math.min(anchor.left, maxLeft));

  const placement: Placement = {
    left,
    width,
    maxHeight: Math.min(preferred, room),
  };
  if (below) placement.top = anchor.bottom + GAP;
  else placement.bottom = layoutHeight - anchor.top + GAP;
  return placement;
}

/** The visible part of the screen, allowing for a keyboard. */
export function visibleArea(): VisibleArea {
  const vv = window.visualViewport;
  if (vv) {
    return {
      top: vv.offsetTop,
      height: vv.height,
      left: vv.offsetLeft,
      width: vv.width,
    };
  }
  return {
    top: 0,
    height: window.innerHeight,
    left: 0,
    width: window.innerWidth,
  };
}
